/**
 * Compact number formatting for the matrix cells' (p5, p50, p95) tuple.
 *
 * The tuple sits on one line inside a ~70px cell. Written out in full, a value
 * like "(-0.62, -0.33, 0.07)" needs roughly 82px and used to spill over the
 * cell border into its neighbours — the main reason the grid looked mushy.
 *
 * Two bounded savings, applied only to the tuple (never to the headline value,
 * which stays exact):
 *   - drop the leading zero:      -0.62  ->  -.62
 *   - one decimal past |100|:    -123.45 -> -123.5
 * Together these cap the tuple's width without truncating anything meaningful.
 */

/** Strip the redundant "0" from "0.62" / "-0.62". */
function dropLeadingZero(str) {
  if (str.startsWith('0.')) return str.slice(1);
  if (str.startsWith('-0.')) return `-${str.slice(2)}`;
  return str;
}

/**
 * Format one tuple component.
 *
 * Precision drops as magnitude grows, so the rendered string stays roughly the
 * same width whether the spread is 0.07 or 1196.40 — the preferred-stock rows
 * trade near $1300 and their tuples were the worst offenders.
 *
 * @param {number|null|undefined} value
 * @param {string} spreadType - 'dollar' | 'percentile' | 'ytc' | 'spot-yield' | other
 */
export function formatTupleNumber(value, spreadType) {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';

  switch (spreadType) {
    case 'percentile':
      return value.toFixed(0);
    case 'ytc':
    case 'spot-yield':
      // Already whole basis points — nothing to compact.
      return (value * 100).toFixed(0);
    case 'dollar':
    default: {
      const abs = Math.abs(value);
      const decimals = abs >= 1000 ? 0 : abs >= 100 ? 1 : 2;
      return dropLeadingZero(value.toFixed(decimals));
    }
  }
}

/**
 * Build the "(p5,mean,p95)" line, or '' when there is nothing to show.
 * No space after the commas — at this size every character counts, and the
 * commas alone separate the values clearly enough.
 */
export function formatTuple(p5, p50, p95, spreadType) {
  if (p5 == null && p50 == null && p95 == null) return '';
  return `(${formatTupleNumber(p5, spreadType)},${formatTupleNumber(p50, spreadType)},${formatTupleNumber(p95, spreadType)})`;
}

/**
 * Shared text rules for matrix cells: digits line up column-to-column instead
 * of shimmering as prices tick, and anything still too wide is clipped at the
 * cell border rather than painted over the next cell.
 */
export const CELL_TEXT_SX = {
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum"',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'clip',
  maxWidth: '100%',
};

// A ticker whose latest price is older than this has stopped trading (or its
// feed has); its cells cannot be scored, which is why its row and column are
// blank. Flag it instead of letting the stale close pass for a current price.
const STALE_PRICE_DAYS = 7;

/** ticker -> 'YYYY-MM-DD' of its last price, for tickers priced more than STALE_PRICE_DAYS ago. */
export function staleTickers(yields, now = Date.now()) {
  const out = {};
  if (!yields) return out;
  for (const t of Object.keys(yields)) {
    const d = yields[t]?.date ? new Date(yields[t].date) : null;
    if (d && !Number.isNaN(d.getTime()) && now - d.getTime() > STALE_PRICE_DAYS * 86400000) {
      out[t] = d.toISOString().slice(0, 10);
    }
  }
  return out;
}

export function staleTitle(ticker, since) {
  return `${ticker}: no market data since ${since}. Its spreads cannot be scored — ` +
    'the ticker has likely been redeemed or delisted; consider removing it from the list.';
}

export const STALE_TICKER_COLOR = '#ffb74d';
